import { type MessageId, type ThreadId } from "@cmmd-forge/contracts";
import { useMemo, useRef } from "react";
import { deriveRevertTurnCountByUserMessageId } from "../ChatView.logic";
import { useTurnDiffSummaries } from "../../hooks/useTurnDiffSummaries";
import { type Thread, type TurnDiffSummary } from "../../types";
import {
  deriveActivePendingGitHubConfirmation,
  type derivePhase,
  type deriveWorkLogEntries,
  deriveTimelineEntriesProjection,
  type hasToolActivityForTurn,
  type TimelineEntriesProjection,
  type TimelineEntry,
} from "../../session-logic";

export function useChatTimelineDerivedState(input: {
  readonly activeThread: Thread | undefined;
  readonly activeLatestTurn: Thread["latestTurn"] | null;
  readonly latestTurnSettled: boolean;
  readonly latestTurnHasToolActivity: ReturnType<typeof hasToolActivityForTurn>;
  readonly timelineMessages: Thread["messages"];
  readonly workLogEntries: ReturnType<typeof deriveWorkLogEntries>;
  readonly inferredPhase: ReturnType<typeof derivePhase>;
}): {
  readonly timelineEntries: TimelineEntry[];
  readonly activePendingGitHubConfirmation: ReturnType<
    typeof deriveActivePendingGitHubConfirmation
  >;
  readonly turnDiffSummaryByAssistantMessageId: Map<MessageId, TurnDiffSummary>;
  readonly revertTurnCountByUserMessageId: Map<MessageId, number>;
} {
  // The projection is what lets the entry derivation reuse ordered work
  // across streaming updates; it is thread-scoped, so a thread switch starts
  // a fresh one rather than splicing two threads' entries. The stored
  // projection includes text-confirmation rows, and the derivation only adds
  // confirmation ids missing from it, so reused projections never duplicate.
  const projectionRef = useRef<{
    threadId: ThreadId | null;
    projection: TimelineEntriesProjection | null;
  }>({ threadId: null, projection: null });
  const timelineEntries = useMemo(() => {
    const threadId = input.activeThread?.id ?? null;
    if (projectionRef.current.threadId !== threadId) {
      projectionRef.current = { threadId, projection: null };
    }
    const projection = deriveTimelineEntriesProjection(
      input.timelineMessages,
      input.activeThread?.proposedPlans ?? [],
      input.workLogEntries,
      projectionRef.current.projection,
    );
    projectionRef.current.projection = projection;
    return projection.entries;
  }, [
    input.activeThread?.id,
    input.activeThread?.proposedPlans,
    input.timelineMessages,
    input.workLogEntries,
  ]);
  const activePendingGitHubConfirmation = useMemo(
    () => deriveActivePendingGitHubConfirmation(timelineEntries, input.inferredPhase),
    [timelineEntries, input.inferredPhase],
  );
  const { turnDiffSummaries, inferredCheckpointTurnCountByTurnId } = useTurnDiffSummaries(
    input.activeThread,
  );
  const turnDiffSummaryByAssistantMessageId = useMemo(() => {
    const byMessageId = new Map<MessageId, TurnDiffSummary>();
    for (const summary of turnDiffSummaries) {
      if (!summary.assistantMessageId) continue;
      byMessageId.set(summary.assistantMessageId, summary);
    }
    return byMessageId;
  }, [turnDiffSummaries]);
  const revertTurnCountByUserMessageId = useMemo(() => {
    return deriveRevertTurnCountByUserMessageId({
      timelineEntries,
      turnDiffSummaryByAssistantMessageId,
      inferredCheckpointTurnCountByTurnId,
    });
  }, [inferredCheckpointTurnCountByTurnId, timelineEntries, turnDiffSummaryByAssistantMessageId]);

  return {
    timelineEntries,
    activePendingGitHubConfirmation,
    turnDiffSummaryByAssistantMessageId,
    revertTurnCountByUserMessageId,
  };
}
